import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from '../services/AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';
import { useSettings } from './SettingsContext';

// Default features matching the database structure
const defaultFeatures = {
    invoicing: false,
    banking_integration: false,
    supplier_invoices: false,
    payroll: false,
    time_tracking: false,
    multi_currency: false,
    api_access: false,
    document_management: false,
    expense_management: false,
    project_management: false,
    dashboard_widget: false,
    invoicing_quotes: false,
    ai_assistant: false,
    bookkeeping: false,
    personell_management: false,
    ai_ocr: false,
    ai_categorization: false,
    ai_reconciliation: false,
    ai_invoice_agent: false,
    email_inbox_scanning: false,
};

const FeatureContext = createContext({
    features: defaultFeatures,
    entitlements: null,
    isLoading: false,
    hasLoaded: false,
    refreshEntitlements: () => { },
});

export const FeatureProvider = ({ children }) => {
    const { companyId, registerRefreshFunction } = useSettings();
    const [features, setFeatures] = useState(defaultFeatures);
    const [entitlements, setEntitlements] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const mountedRef = useRef(true);

    const fetchEntitlements = useCallback(async () => {
        if (!companyId) {
            return;
        }

        try {
            setIsLoading(true);

            // Try new entitlements API first
            try {
                const url = getApiUrl(`${companyId}/entitlements`);
                const { data } = await axios.get(url, { withCredentials: true });

                if (mountedRef.current && data?.features) {
                    setFeatures({
                        ...defaultFeatures,
                        ...data.features
                    });
                    setEntitlements(data);
                    return;
                }
            } catch (entitlementError) {
                // Fall back to old settings API if new API not available
                // console.log('Falling back to legacy settings API');

            }

            // Fallback: Old settings bulk API
            const url = getApiUrl(`${companyId}/settings/bulk`);
            const { data } = await axios.post(
                url,
                { keys: ['enabled_features'] },
                { withCredentials: true }
            );

            if (mountedRef.current && data?.settings?.enabled_features) {
                let parsedFeatures;
                if (typeof data.settings.enabled_features === 'string') {
                    parsedFeatures = JSON.parse(data.settings.enabled_features);
                } else {
                    parsedFeatures = data.settings.enabled_features;
                }
                setFeatures({
                    ...defaultFeatures,
                    ...parsedFeatures
                });
                // Set basic entitlements structure for legacy fallback
                setEntitlements({
                    features: parsedFeatures,
                    plan: { name: 'legacy', displayName: 'Legacy' },
                    aiCredits: null
                });
            }
        } catch (error) {
            console.error('Error fetching features:', error);
            if (mountedRef.current) {
                setFeatures(defaultFeatures);
            }
        } finally {
            if (mountedRef.current) {
                setIsLoading(false);
                setHasLoaded(true);
            }
        }
    }, [companyId]);

    useEffect(() => {
        mountedRef.current = true;
        fetchEntitlements();

        if (registerRefreshFunction) {
            const unregister = registerRefreshFunction('features', fetchEntitlements);
            return () => {
                mountedRef.current = false;
                if (unregister) unregister();
            };
        }

        return () => {
            mountedRef.current = false;
        };
    }, [companyId, fetchEntitlements, registerRefreshFunction]);

    const value = {
        features,
        entitlements,
        isLoading,
        hasLoaded,
        refreshEntitlements: fetchEntitlements,
    };

    return (
        <FeatureContext.Provider value={value}>
            {children}
        </FeatureContext.Provider>
    );
};

export const useFeatures = () => useContext(FeatureContext);

export default FeatureContext;
